import React, { useCallback } from 'react';
import BudgetDonations from './BudgetDonations';
import BudgetIntro from './BudgetIntro';
import BudgetOverheadCost from './BudgetOverheadCost';
import BudgetUpload from './BudgetUpload';
import ProgramCost from './ProgramCost';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Question } from '../Beneficiary/BeneficiaryLanding';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import {
  BudgetSubmitReference,
  BudgetValue,
  DIRECT_COST_QUESTION_ID,
  FINAL_BUDGET_QUESTION_ID,
  FinalBudget,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
  defaultBudgetValue,
  getBudgetDollarAmount,
  getFinalizedBudget,
  saveBudgetAmountValue,
  saveFinalBudgeAmount,
} from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import BudgetSummary from './BudgetSummary';
import { updateIndicatorAnswer } from '../ImpactIndicator/api';
import { FeedbackUpdateAnswerResponse } from '../../../NPOFeedback/MakeEdits/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { GuidanceTextResponse } from '../utils';
import { FeedbackCommentBodyWithIndex } from '../../../NPOFeedback/utils';
import { parseString } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

const marginTop: React.CSSProperties = {
  marginTop: SPACERS.large,
};

const defaultFileValue: TrueImpactFile = {
  fileName: undefined,
  description: undefined,
};

interface BudgetProps {
  hideBottomBar?: boolean;
  value?: BudgetValue | string;
  guidanceText: GuidanceTextResponse;
  allValues?: { [id: number]: string | undefined };
  question: Question;
  reportId: number | string;
  programName: string;
  isReportPassed?: boolean;
  feedbackByQuestionId: {
    [questionId: number]: FeedbackCommentBodyWithIndex[];
  };
  questionRef?: React.RefObject<BudgetSubmitReference>;
  handleBack: () => unknown;
  handleNext: (buttonPressed: boolean) => unknown;
  handleValueChange: (questionId: number, value: string) => unknown;
  onNextSection: () => unknown;
  onPreviousSection: () => unknown;
}

const getFileInfo = (
  value: BudgetProps['value'],
): TrueImpactFile | undefined => {
  if (!value) return defaultFileValue;
  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      return parsedValue.fileName
        ? parsedValue
        : parsedValue.description
        ? {
            description: parsedValue.description,
          }
        : defaultFileValue;
    } catch (e) {
      return defaultFileValue;
    }
  }
  return typeof value.text === 'string' ? defaultFileValue : value.text;
};

export type FeedbackMakeEditsBudgetHandler = React.ElementRef<
  | typeof BudgetIntro
  | typeof ProgramCost
  | typeof BudgetDonations
  | typeof BudgetOverheadCost
  | typeof BudgetUpload
>;

const Budget: React.FC<BudgetProps> = ({
  value,
  allValues,
  question,
  guidanceText,
  questionRef,
  reportId,
  hideBottomBar,
  isReportPassed,
  feedbackByQuestionId,
  handleBack,
  handleValueChange,
  onNextSection,
  onPreviousSection,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<
    FeedbackUpdateAnswerResponse
  >();

  const dollar = getBudgetDollarAmount(value);
  const finalBudget = getFinalizedBudget(allValues?.[FINAL_BUDGET_QUESTION_ID]);
  const useFinalBudget =
    isReportPassed && finalBudget ? finalBudget : undefined;

  const handleIntroNext = useCallback(
    (value: string) => {
      handleValueChange(question.dbId, value);
    },
    [handleValueChange, question.dbId],
  );

  const handleFileUploadNext = useCallback(
    (
      fileName: string | undefined,
      description: string | undefined,
      id: number | undefined,
      url: string | undefined,
    ) => {
      handleValueChange(
        question.dbId,
        JSON.stringify({ fileName, description, id, url }),
      );
    },
    [onNextSection],
  );

  const handleAmountNext = useCallback(
    (value: string) => {
      if (isReportPassed) {
        const answer = JSON.stringify(
          saveFinalBudgeAmount(
            question.dbId,
            value,
            allValues?.[FINAL_BUDGET_QUESTION_ID],
          ),
        );
        // TIPD-4144
        handleValueChange(FINAL_BUDGET_QUESTION_ID, answer);
        handleValueChange(question.dbId, saveBudgetAmountValue(dollar));
        client &&
          makeRequest(
            updateIndicatorAnswer(
              client,
              undefined,
              true,
              FINAL_BUDGET_QUESTION_ID,
              reportId,
              answer,
            ),
          );
      } else {
        handleValueChange(question.dbId, saveBudgetAmountValue(value));
      }
    },
    [handleValueChange, client, allValues, isReportPassed, question.dbId],
  );

  const handleSummaryNext = useCallback(
    (value: string) => {
      handleValueChange(question.dbId, value);
    },
    [handleValueChange, question.dbId],
  );

  const budgetSummary = allValues?.[80011]
    ? (parseString(allValues?.[80011]) as FinalBudget)
    : undefined;

  switch (question.dbId) {
    case 80001:
      return (
        <BudgetIntro
          ref={questionRef}
          reportId={reportId}
          guidanceText={guidanceText}
          hideBottomBar={hideBottomBar}
          isReportPassed={isReportPassed}
          onNext={handleIntroNext}
          onPrevious={onPreviousSection}
        />
      );
    case DIRECT_COST_QUESTION_ID: {
      const directCost = budgetSummary?.directCost;
      return (
        <ProgramCost
          ref={questionRef}
          initialValue={
            isReportPassed
              ? useFinalBudget?.directCost ||
                directCost ||
                dollar ||
                defaultBudgetValue
              : dollar || directCost || ''
          }
          reportId={reportId}
          guidanceText={guidanceText}
          hideBottomBar={hideBottomBar}
          isReportPassed={isReportPassed}
          onNext={handleAmountNext}
          onPrevious={handleBack}
        />
      );
    }
    case IN_KIND_DONATIONS_QUESTION_ID: {
      const inKindDonations = budgetSummary?.inKindDonations;
      return (
        <BudgetDonations
          ref={questionRef}
          initialValue={
            isReportPassed
              ? useFinalBudget?.inKindDonations ||
                inKindDonations ||
                dollar ||
                defaultBudgetValue
              : dollar || inKindDonations || ''
          }
          reportId={reportId}
          guidanceText={guidanceText}
          hideBottomBar={hideBottomBar}
          isReportPassed={isReportPassed}
          onNext={handleAmountNext}
          onPrevious={handleBack}
        />
      );
    }
    case OVERHEAD_COST_QUESTION_ID: {
      const overheadCost = budgetSummary?.overheadCost;
      return (
        <BudgetOverheadCost
          ref={questionRef}
          initialValue={
            isReportPassed
              ? useFinalBudget?.overheadCost ||
                overheadCost ||
                dollar ||
                defaultBudgetValue
              : dollar || overheadCost || ''
          }
          reportId={reportId}
          guidanceText={guidanceText}
          hideBottomBar={hideBottomBar}
          isReportPassed={isReportPassed}
          onNext={handleAmountNext}
          onPrevious={handleBack}
        />
      );
    }
    case 80009:
      return (
        <BudgetUpload
          ref={questionRef}
          initialValue={getFileInfo(value)}
          reportId={reportId}
          guidanceText={guidanceText}
          hideBottomBar={hideBottomBar}
          isReportPassed={isReportPassed}
          onNext={handleFileUploadNext}
          onPrevious={handleBack}
        />
      );
    case 80011:
      return (
        <BudgetSummary
          ref={questionRef}
          values={allValues}
          initialValue={value}
          reportId={reportId}
          guidanceText={guidanceText}
          hideBottomBar={hideBottomBar}
          isReportPassed={isReportPassed}
          feedbackByQuestionId={feedbackByQuestionId}
          onNext={handleSummaryNext}
          onPrevious={handleBack}
        />
      );

    default:
      return (
        <TrueImpactText
          variant="body-large"
          style={marginTop}
          align="center"
          color="critical"
        >
          Error code: {question.dbId} - Please try refreshing the page.
        </TrueImpactText>
      );
  }
};

export default Budget;
